import { jwtDecode } from 'jwt-decode';

const API_URL = process.env.REACT_APP_DJANGO_API_URL;

// Helper function to get cookie value by name
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

export const getStoredToken = () => {
  // First check for Google token in localStorage
  const googleToken = localStorage.getItem('token');
  if (googleToken) return googleToken;
  
  // Then check for Apple token in localStorage
  const appleTokenLocal = localStorage.getItem('apple_token');
  if (appleTokenLocal) return appleTokenLocal;
  
  // Finally check for Apple token in cookies
  return getCookie('apple_token');
};

export const getStoredRefreshToken = () => {
  // Check localStorage first
  const refreshTokenLocal = localStorage.getItem('apple_refresh_token');
  if (refreshTokenLocal) return refreshTokenLocal;
  
  // Then check cookies
  return getCookie('apple_refresh_token');
};

export const getTokenExpiration = () => {
  // Check localStorage first
  const expiresAtLocal = localStorage.getItem('apple_token_expires_at');
  if (expiresAtLocal) return expiresAtLocal;
  
  // Then check cookies
  return getCookie('apple_token_expires_at');
};

export const isTokenExpired = () => {
  const token = getStoredToken();
  if (!token) return true;

  try {
    // Check JWT expiration
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    const jwtExpired = decoded.exp < currentTime;
    
    // Check apple_token_expires_at if it exists
    const appleExpiresAt = getTokenExpiration();
    if (appleExpiresAt) {
      const appleExpired = new Date(appleExpiresAt).getTime() < Date.now();
      return jwtExpired || appleExpired;
    }
    
    return jwtExpired;
  } catch (error) {
    console.error('Error decoding token:', error);
    return true;
  }
};

export const refreshToken = async () => {
  const refresh_token = getStoredRefreshToken();
  if (!refresh_token) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await fetch(`${API_URL}/api/refresh_token/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh_token,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const data = await response.json();
    
    // Store new tokens
    if (data.id_token) {
      localStorage.setItem('apple_token', data.id_token);
      // Also set cookie
      document.cookie = `apple_token=${data.id_token}; path=/; max-age=3600; samesite=Lax`;
    }
    if (data.refresh_token) {
      localStorage.setItem('apple_refresh_token', data.refresh_token);
      // Also set cookie
      document.cookie = `apple_refresh_token=${data.refresh_token}; path=/; max-age=3600; samesite=Lax`;
    }
    if (data.expires_in) {
      const expiresAt = new Date(Date.now() + data.expires_in * 1000).toISOString();
      localStorage.setItem('apple_token_expires_at', expiresAt);
      // Also set cookie
      document.cookie = `apple_token_expires_at=${expiresAt}; path=/; max-age=3600; samesite=Lax`;
    }

    return data.id_token;
  } catch (error) {
    console.error('Error refreshing token:', error);
    throw error;
  }
};

export const getAuthHeader = () => {
  const token = getStoredToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export const setupTokenRefresh = () => {
  // Check token expiration every minute
  setInterval(async () => {
    if (isTokenExpired()) {
      try {
        await refreshToken();
      } catch (error) {
        console.error('Failed to refresh token:', error);
        // Redirect to login if refresh fails
        window.location.href = '/login';
      }
    }
  }, 60000); // Check every minute
}; 